import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { 
  DocumentTextIcon, 
  PlusIcon, 
  EyeIcon, 
  PencilIcon, 
  PrinterIcon,
  CheckCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  BookOpenIcon,
  DocumentArrowDownIcon,
  CodeBracketIcon,
  ClockIcon as HistoryIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { DocumentSidebar } from "../../../../components/dashboard/documents/DocumentSidebar";
import NewHseDocumentDialog from "../../../../components/dashboard/documents/NewHseDocumentDialog";
import { HseDocumentJsonDialog } from "../../../../components/dashboard/documents/HseDocumentJsonDialog";
import { getHseDocuments, printDocument, printMultipleDocuments } from "../../../../api/hse/documents";
import { getDisciplines } from "../../../../api/hse/disciplines";
import { resolveDocumentFilterContext, formatHseDocumentDisplayNumber } from "../../../../utils/documentStructureUtils";
import { HseTabBar } from "../../../../components/dashboard/hse/HseTabBar";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { HseRecordActionsMenu } from "../../../../components/dashboard/hse/HseRecordActionsMenu";
import { DocumentComplianceDialog } from "../../../../components/dashboard/hse/compliance/DocumentComplianceDialog";

const DOCUMENT_LIST_STATE_KEY = "promang:hse-documents:list";

const readDocumentListState = () => {
  try {
    const raw = window.sessionStorage.getItem(DOCUMENT_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/**
 * HSE/KS Documents List - Controlled documents library
 */
export const HseKsDocumentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const initialListStateRef = useRef(readDocumentListState());
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => initialListStateRef.current.searchQuery || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => initialListStateRef.current.searchQuery || "");
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const infoTooltipRef = useRef(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(
    () => initialListStateRef.current.selectedDiscipline ?? null
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => initialListStateRef.current.selectedCategory ?? null
  );
  const [selectedChapter, setSelectedChapter] = useState(
    () => initialListStateRef.current.selectedChapter ?? null
  );
  const [printingDocumentId, setPrintingDocumentId] = useState(null);
  const [selectedDocumentsForPrint, setSelectedDocumentsForPrint] = useState(new Set());
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [isPrintingBook, setIsPrintingBook] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [showNewDocumentDialog, setShowNewDocumentDialog] = useState(false);
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  
  // All hooks must be called before any conditional logic
  const canCreate = useUserHasAccess({ role: "hse_ks_documents", action: "create" });
  const canUpdate = useUserHasAccess({ role: "hse_ks_documents", action: "update" });
  const canApproveRole = useUserHasAccess({ role: "hse_ks_documents_approve", action: "approve" });
  
  // Conditional logic after hooks
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canApprove = isAdmin || canApproveRole;
  const canManage = isAdmin || canApprove;

  const [activeFilter, setActiveFilter] = useState(() => {
    const path = location.pathname;
    if (path.includes("/policies")) return "policies";
    if (path.includes("/procedures")) return "procedures";
    if (path.includes("/instructions")) return "instructions";
    if (path.includes("/forms")) return "forms";
    return "all";
  });

  const filters = [
    { id: "all", label: t("hse.documents.tab.all"), path: "/dashboard/hse-ks/documents" },
    { id: "policies", label: t("hse.documents.tab.policies"), path: "/dashboard/hse-ks/documents/policies" },
    { id: "procedures", label: t("hse.documents.tab.procedures"), path: "/dashboard/hse-ks/documents/procedures" },
    { id: "instructions", label: t("hse.documents.tab.instructions"), path: "/dashboard/hse-ks/documents/instructions" },
    { id: "forms", label: t("hse.documents.tab.forms"), path: "/dashboard/hse-ks/documents/forms" },
    { id: "guidance", label: t("hse.documents.tab.guidance"), path: "/dashboard/hse-ks/documents/guidance" },
    { id: "record", label: t("hse.documents.tab.record"), path: "/dashboard/hse-ks/documents/record" },
    { id: "template", label: t("hse.documents.tab.template"), path: "/dashboard/hse-ks/documents/template" },
  ];

  useEffect(() => {
    fetchDisciplines();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [activeFilter, debouncedSearchQuery, selectedDiscipline, selectedCategory, selectedChapter]);

  const filterContext = useMemo(
    () =>
      resolveDocumentFilterContext(disciplines, {
        selectedDiscipline,
        selectedCategory,
        selectedChapter,
      }),
    [disciplines, selectedDiscipline, selectedCategory, selectedChapter]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        DOCUMENT_LIST_STATE_KEY,
        JSON.stringify({
          searchQuery,
          selectedDiscipline,
          selectedCategory,
          selectedChapter,
        })
      );
    } catch {
      // Best-effort UI state only.
    }
  }, [searchQuery, selectedDiscipline, selectedCategory, selectedChapter]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedChapter(null);
    // Filtrer dokumenter basert på kategori
  };

  const handleChapterSelect = (categoryId, chapterNumber) => {
    setSelectedCategory(categoryId);
    setSelectedChapter(chapterNumber);
    // Filtrer dokumenter basert på kategori og kapittel
    // Dokumenter vil automatisk filtreres i fetchDocuments når selectedChapter endres
  };

  const handleAddDocument = (categoryId, chapterNumber) => {
    setShowNewDocumentDialog(true);
  };

  const fetchDisciplines = async () => {
    try {
      const response = await getDisciplines();
      setDisciplines(response.company_disciplines || []);
    } catch (err) {
      console.error("Error fetching disciplines for filter labels:", err);
    }
  };

  const handleAddChapter = (categoryId, parentChapterNumber) => {
    // This is handled by DocumentSidebar component now
    // The sidebar will show a dialog and handle the creation
    console.log("Add chapter:", { categoryId, parentChapterNumber });
  };

  const handleEditChapter = async (categoryId, chapterNumber, newData) => {
    try {
      // Find chapter ID from disciplines data
      // This will be handled by DocumentSidebar component
      console.log("Edit chapter:", { categoryId, chapterNumber, newData });
      toast.success(t("Kapittel oppdatert"));
      // Refresh documents after edit
      fetchDocuments();
    } catch (err) {
      console.error("Error editing chapter:", err);
      toast.error(t("Kunne ikke oppdatere kapittel"));
    }
  };

  const handleDeleteChapter = async (categoryId, chapterNumber) => {
    try {
      // This will be handled by DocumentSidebar component
      console.log("Delete chapter:", { categoryId, chapterNumber });
      toast.success(t("Kapittel slettet"));
      // Clear selection if deleted chapter was selected
      if (selectedChapter === chapterNumber) {
        setSelectedChapter(null);
      }
      // Refresh documents after delete
      fetchDocuments();
    } catch (err) {
      console.error("Error deleting chapter:", err);
      toast.error(t("Kunne ikke slette kapittel"));
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoTooltipRef.current && !infoTooltipRef.current.contains(event.target)) {
        setShowInfoTooltip(false);
      }
    };

    if (showInfoTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInfoTooltip]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Build filter parameters
      const filters = {};
      
      if (activeFilter !== "all") {
        // Map filter IDs to document types
        const documentTypeMap = {
          policies: "policy",
          procedures: "procedure",
          instructions: "instruction",
          forms: "form",
          guidance: "guidance",
          record: "record",
          template: "template"
        };
        filters.document_type = documentTypeMap[activeFilter] || activeFilter;
      }
      
      if (debouncedSearchQuery) {
        filters.search = debouncedSearchQuery;
      }
      
      if (selectedCategory != null && selectedCategory !== "") {
        filters.category = selectedCategory;
      }
      
      if (selectedChapter != null && selectedChapter !== "") {
        // API forventer kapittelnummer som streng (samme som Chapter.number i databasen)
        filters.chapter = String(selectedChapter);
      }
      
      // Only add discipline_id filter if a specific discipline is selected (not "Alle")
      if (selectedDiscipline && selectedDiscipline !== "" && selectedDiscipline !== null) {
        filters.discipline_id = selectedDiscipline;
      }
      
      const response = await getHseDocuments(filters);
      
      // Check for errors in response
      if (response.errors && response.errors.length > 0) {
        setError(true);
        const errorMessage = response.errors[0] || t("Kunne ikke laste dokumenter");
        toast.error(errorMessage);
        setDocuments([]);
        return;
      }
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        console.error("Invalid response structure:", response);
        setError(true);
        const errorMsg = t("Ugyldig respons fra server. Vennligst prøv igjen eller kontakt support hvis problemet vedvarer.");
        toast.error(errorMsg);
        setDocuments([]);
        return;
      }
      
      // Set documents (empty array if documents is undefined/null)
      setDocuments(response.documents || []);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(true);
      
      // Extract detailed error information
      let errorMessage = t("Kunne ikke laste dokumenter");
      
      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const data = err.response.data;
        
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessage = data.errors[0];
        } else if (data?.error) {
          errorMessage = data.error;
        } else {
          // Provide status-specific messages
          switch (status) {
            case 400:
              errorMessage = t("Ugyldig forespørsel. Vennligst sjekk at alle filtre er korrekte.");
              break;
            case 401:
              errorMessage = t("Du er ikke autentisert. Vennligst logg inn på nytt.");
              break;
            case 403:
              errorMessage = t("Du har ikke tilgang til å se disse dokumentene.");
              break;
            case 404:
              errorMessage = t("Ressursen ble ikke funnet. Vennligst prøv igjen.");
              break;
            case 500:
              errorMessage = t("Serverfeil. Vennligst prøv igjen senere eller kontakt support hvis problemet vedvarer.");
              break;
            default:
              errorMessage = t("En feil oppstod ved lasting av dokumenter. Status: {status}", { status });
          }
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = t("Kunne ikke koble til serveren. Vennligst sjekk internettforbindelsen og prøv igjen.");
      } else {
        // Error in request setup
        errorMessage = err.message || errorMessage;
      }
      
      toast.error(errorMessage);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (documentId) => {
    // Prevent multiple clicks
    if (printingDocumentId === documentId) {
      return;
    }
    
    try {
      // Only print approved documents
      const doc = documents.find(d => d.id === documentId);
      if (!doc) {
        toast.error(t("Dokument ikke funnet"));
        return;
      }
      
      // Check if document has approved version
      // has_approved_version can be undefined if not set by API, so we check status as fallback
      // Only documents with approved version can be printed, even for admin
      const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
      if (!hasApprovedVersion) {
        toast.error(t("Kun godkjente dokumenter kan skrives ut"));
        return;
      }
      
      setPrintingDocumentId(documentId);
      const response = await printDocument(documentId, null, 'document');
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokument skrives ut..."));
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing document:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokument");
      toast.error(errorMessage);
    } finally {
      setPrintingDocumentId(null);
    }
  };
  
  const handlePrintMultiple = async (documentIds = null) => {
    try {
      setIsPrintingBook(true);
      
      // If no documentIds provided, use selected documents, or all approved documents if none selected
      let docsToPrint = [];
      
      // Helper function to check if document has approved version
      const hasApprovedVersion = (doc) => {
        return doc.has_approved_version === true || doc.status === 'approved';
      };
      
      if (documentIds && documentIds.length > 0) {
        // Use provided document IDs
        docsToPrint = documents.filter(doc => documentIds.includes(doc.id));
      } else if (selectedDocumentsForPrint.size > 0) {
        // Use selected documents
        docsToPrint = documents.filter(doc => selectedDocumentsForPrint.has(doc.id));
      } else {
        // Use all approved documents (only approved, no admin exception)
        docsToPrint = documents.filter(doc => hasApprovedVersion(doc));
      }
      
      // Filter to only approved documents (no admin exception)
      const approvedDocs = docsToPrint.filter(doc => hasApprovedVersion(doc));
      
      if (approvedDocs.length === 0) {
        toast.error(t("Ingen godkjente dokumenter å skrive ut"));
        return;
      }
      
      const response = await printMultipleDocuments(
        approvedDocs.map(d => d.id),
        'book'
      );
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokumentbok skrives ut..."));
        // Clear selection after successful print
        setSelectedDocumentsForPrint(new Set());
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing multiple documents:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokumentbok");
      toast.error(errorMessage);
    } finally {
      setIsPrintingBook(false);
    }
  };
  
  const handleToggleDocumentSelection = (documentId) => {
    const newSelection = new Set(selectedDocumentsForPrint);
    if (newSelection.has(documentId)) {
      newSelection.delete(documentId);
    } else {
      newSelection.add(documentId);
    }
    setSelectedDocumentsForPrint(newSelection);
  };
  
  const handleSelectAllDocuments = () => {
    // Select all documents that can be printed (only approved versions, no admin exception)
    const printableDocs = documents.filter(doc => {
      const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
      return hasApprovedVersion;
    });
    
    if (selectedDocumentsForPrint.size === printableDocs.length && 
        printableDocs.every(doc => selectedDocumentsForPrint.has(doc.id))) {
      // Deselect all
      setSelectedDocumentsForPrint(new Set());
    } else {
      // Select all printable
      setSelectedDocumentsForPrint(new Set(printableDocs.map(d => d.id)));
    }
  };

  const headerActions = [
    {
      label: "Guide",
      icon: BookOpenIcon,
      path: "/dashboard/hse-ks/documents/guide",
      hidden: false,
      menu: false,
    },
    {
      label: "Maler",
      path: "/dashboard/hse-ks/documents/templates",
      hidden: false,
      menu: false,
    },
    ...(canManage
      ? [
          {
            label: t("hse.document_read_overview.title"),
            icon: CheckCircleIcon,
            path: "/dashboard/hse-ks/documents/read-status",
            hidden: false,
            menu: false,
          },
        ]
      : []),
    {
      component: null,
      label: isPrintingBook ? t("Genererer dokumentbok...") : t("Skriv ut dokument-bok"),
      action: () => handlePrintMultiple(),
      icon: DocumentArrowDownIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      disabled: isPrintingBook || documents.length === 0,
      loading: isPrintingBook,
      menu: false,
    },
    ...(canCreate ? [{
      label: t("hse.documents.json.open_dialog"),
      icon: CodeBracketIcon,
      action: () => setShowJsonDialog(true),
      hidden: false,
      menu: false,
    }] : []),
    ...(canCreate ? [{
      label: t("hse.documents.new"),
      icon: PlusIcon,
      action: () => setShowNewDocumentDialog(true),
      hidden: false,
      menu: false,
    }] : []),
  ];

  return (
    <HsePageChrome
      title={t("hse.documents.title")}
      subtitle={t("hse.documents.subtitle")}
      actions={headerActions}
    >
      <div className="flex h-full mt-8">
                {/* Sidebar */}
                <DocumentSidebar 
                  onDisciplineSelect={setSelectedDiscipline}
                  selectedDisciplineId={selectedDiscipline}
                  onCategorySelect={handleCategorySelect}
                  onChapterSelect={handleChapterSelect}
                  onAddChapter={handleAddChapter}
                  onEditChapter={handleEditChapter}
                  onDeleteChapter={handleDeleteChapter}
                  onAddDocument={handleAddDocument}
                />
        
        {/* Main content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 max-w-7xl mx-auto">
          {/* Filters */}
          <HseTabBar
            tabs={filters}
            activeId={activeFilter}
            onSelect={(tab) => {
              setActiveFilter(tab.id);
              navigate(tab.path);
            }}
            ariaLabel={t("hse.documents.nav.aria")}
            trailing={
                <div className="relative" ref={infoTooltipRef}>
                  <button
                    type="button"
                    onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    aria-label="Informasjon om dokumenttyper"
                  >
                    <InformationCircleIcon className="h-5 w-5" />
                  </button>
                  {showInfoTooltip && (
                    <div className="absolute right-0 top-8 z-10 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Policyer</h4>
                          <p className="text-xs text-gray-600">
                            Overordnede retningslinjer og prinsipper som beskriver organisasjonens mål og verdier. 
                            Policyer gir rammeverket for beslutninger og handlinger.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Prosedyrer</h4>
                          <p className="text-xs text-gray-600">
                            Detaljerte beskrivelser av hvordan spesifikke oppgaver skal utføres. 
                            Prosedyrer gir trinn-for-trinn instruksjoner for å sikre konsistent utførelse.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Instrukser</h4>
                          <p className="text-xs text-gray-600">
                            Konkrete veiledninger for hvordan enkeltoperasjoner eller prosesser skal utføres. 
                            Instrukser er mer detaljerte enn prosedyrer og fokuserer på spesifikke oppgaver.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Skjemaer</h4>
                          <p className="text-xs text-gray-600">
                            Standardiserte dokumenter som brukes til innsamling av informasjon, 
                            registrering av data eller dokumentasjon av prosesser. Skjemaer sikrer konsistent datainnsamling.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowInfoTooltip(false)}
                        className="mt-3 w-full text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Lukk
                      </button>
                    </div>
                  )}
                </div>
            }
          />

          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("Søk i dokumenter...")}
            initialFilters={{ query: searchQuery }}
            onFiltersChange={(next) => setSearchQuery(next.query || "")}
          />

                    {/* Filter Info */}
                    {filterContext.hasSidebarFilter && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1 space-y-2">
                            {filterContext.summaryLabel && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">
                                  {t("Viser dokumenter for")}
                                </p>
                                <p className="text-sm font-medium text-blue-950 break-words">
                                  {filterContext.summaryLabel}
                                </p>
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-blue-900">
                                {t("Filtrert på")}:
                              </span>
                              {filterContext.discipline && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                  {t("Fag/Bransje")}: {filterContext.discipline.name}
                                </span>
                              )}
                              {filterContext.category && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                  {t("Kategori")}: {filterContext.category.name}
                                </span>
                              )}
                              {selectedChapter && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                  {t("Kapittel")}:{" "}
                                  {filterContext.chapter?.title
                                    ? `${filterContext.chapter.number} - ${filterContext.chapter.title}`
                                    : selectedChapter}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedDiscipline(null);
                              setSelectedCategory(null);
                              setSelectedChapter(null);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                          >
                            {t("Fjern filter")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Documents List */}
                    <div className="bg-white shadow sm:rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        {loading ? (
                          <ResourceLoading size="sm" />
                        ) : error ? (
                          <ResourceError 
                            errors={[t("Kunne ikke laste dokumenter")]} 
                            subtitle={t("Det oppstod en feil ved lasting av dokumenter. Vennligst prøv å oppdatere siden eller kontakt support hvis problemet vedvarer.")}
                          />
                        ) : documents.length === 0 ? (
                          <ResourceEmpty 
                            message={
                              selectedDiscipline && selectedDiscipline !== "" && selectedDiscipline !== null
                                ? selectedChapter 
                                  ? t(`Ingen dokumenter funnet for kapittel ${selectedChapter}`)
                                  : selectedCategory
                                    ? t("Ingen dokumenter funnet for valgt kategori")
                                    : t("Ingen dokumenter funnet for valgt fag/bransje")
                                : selectedChapter 
                                  ? t(`Ingen dokumenter funnet for kapittel ${selectedChapter}`)
                                  : t("Ingen dokumenter funnet")
                            } 
                          />
                        ) : (
                          <>
                            {/* Selection controls */}
                            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                              <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      documents.length > 0 &&
                                      documents.filter(doc => {
                                        const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
                                        return hasApprovedVersion;
                                      }).length > 0 &&
                                      documents.filter(doc => {
                                        const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
                                        return hasApprovedVersion;
                                      }).every(doc => selectedDocumentsForPrint.has(doc.id))
                                    }
                                    onChange={handleSelectAllDocuments}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">
                                    {t("Velg alle")}
                                  </span>
                                </label>
                                {selectedDocumentsForPrint.size > 0 && (
                                  <span className="text-sm text-gray-500">
                                    {selectedDocumentsForPrint.size} {t("valgt")}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handlePrintMultiple()}
                                disabled={isPrintingBook}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isPrintingBook ? (
                                  <>
                                    <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Genererer...")}
                                  </>
                                ) : (
                                  <>
                                    <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                                    {t("Skriv ut dokument-bok")}
                                  </>
                                )}
                              </button>
                              {isAdmin && selectedDocumentsForPrint.size > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setShowComplianceDialog(true)}
                                  className="pm-btn pm-btn--secondary pm-btn--sm ml-2"
                                >
                                  <UserGroupIcon className="h-4 w-4" aria-hidden="true" />
                                  {t("hseCompliance.dialog.bulk_action", { count: selectedDocumentsForPrint.size })}
                                </button>
                              ) : null}
                            </div>
                            
                            <ul className="divide-y divide-gray-200">
                              {documents.map((document) => {
                                const displayNumber = formatHseDocumentDisplayNumber(document);
                                // Check if document has approved version
                                // has_approved_version can be undefined if not set by API, so we check status as fallback
                                // Only documents with approved version can be printed or selected, even for admin
                                const hasApprovedVersion = document.has_approved_version === true || document.status === 'approved';
                                const canPrint = hasApprovedVersion; // No admin exception
                                const isSelected = selectedDocumentsForPrint.has(document.id);
                                
                                return (
                                <li
                                  key={document.id}
                                  className={`cursor-pointer px-6 py-4 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                                  onClick={() => navigate(`/dashboard/hse-ks/documents/${document.id}`)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                      <div className="flex-shrink-0" onClick={(event) => event.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleToggleDocumentSelection(document.id)}
                                          disabled={!canPrint}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                          title={!canPrint ? t("Kun godkjente dokumenter kan velges") : t("Velg dokument")}
                                        />
                                      </div>
                                      <div className="flex-shrink-0">
                                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                                      </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {document.title}
                                        </p>
                                        {document.status === "approved" && (
                                          <CheckCircleIcon className="h-4 w-4 text-green-500" title="Godkjent" />
                                        )}
                                        {document.status === "pending_approval" && (
                                          <ClockIcon className="h-4 w-4 text-yellow-500" title="Venter på godkjenning" />
                                        )}
                                      </div>
                                      {document.description && (
                                        <p className="text-sm text-gray-500 truncate mt-1">
                                          {document.description}
                                        </p>
                                      )}
                                      <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                        {displayNumber ? (
                                          <span className="font-mono text-gray-700">
                                            {displayNumber}
                                          </span>
                                        ) : null}
                                        <span>{t("Versjon")} {document.current_version}</span>
                                        {document.document_type_display && (
                                          <span>{document.document_type_display}</span>
                                        )}
                                        {document.chapter &&
                                          (document.chapter.number != null || document.chapter.title) && (
                                          <span className="font-mono">
                                            {document.chapter.number != null ? `${document.chapter.number} - ` : ""}
                                            {document.chapter.title ?? ""}
                                          </span>
                                        )}
                                        {document.category_display && (
                                          <span>{document.category_display}</span>
                                        )}
                                        {document.updated_at && (
                                          <span>
                                            {t("Oppdatert")}: {new Date(document.updated_at).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <HseRecordActionsMenu
                                    items={[
                                      {
                                        key: "open",
                                        label: t("hse.actions.open"),
                                        icon: EyeIcon,
                                        onClick: () => navigate(`/dashboard/hse-ks/documents/${document.id}`),
                                      },
                                      {
                                        key: "edit",
                                        label: t("hse.actions.edit"),
                                        icon: PencilIcon,
                                        hidden: !canUpdate,
                                        onClick: () => navigate(`/dashboard/hse-ks/documents/${document.id}/edit`),
                                      },
                                      {
                                        key: "export_pdf",
                                        label:
                                          printingDocumentId === document.id
                                            ? t("hse.actions.export_pdf_loading")
                                            : t("hse.actions.export_pdf"),
                                        icon: PrinterIcon,
                                        disabled: !canPrint || printingDocumentId === document.id,
                                        onClick: () => handlePrint(document.id),
                                      },
                                      {
                                        key: "approve",
                                        label: t("hse.actions.approve"),
                                        icon: CheckCircleIcon,
                                        hidden: !canApprove || document.status !== "pending_approval",
                                        onClick: () => navigate(`/dashboard/hse-ks/documents/${document.id}/approve`),
                                      },
                                      {
                                        key: "revisions",
                                        label: t("hse.actions.revisions"),
                                        icon: HistoryIcon,
                                        onClick: () =>
                                          navigate(`/dashboard/hse-ks/documents/${document.id}#document-revisions`),
                                      },
                                    ]}
                                  />
                                </div>
                              </li>
                              );
                            })}
                          </ul>
                          </>
                        )}
                      </div>
                    </div>
          </div>
        </div>
      </div>

      <NewHseDocumentDialog
        open={showNewDocumentDialog}
        onClose={() => setShowNewDocumentDialog(false)}
        filterContext={filterContext}
        defaultDisciplineId={selectedDiscipline || ""}
        defaultCategoryId={selectedCategory || ""}
        defaultChapterNumber={selectedChapter || ""}
      />
      {isAdmin ? (
        <DocumentComplianceDialog
          open={showComplianceDialog}
          documentIds={[...selectedDocumentsForPrint]}
          onClose={() => setShowComplianceDialog(false)}
          onSaved={() => setSelectedDocumentsForPrint(new Set())}
        />
      ) : null}
      <HseDocumentJsonDialog
        open={showJsonDialog}
        onClose={() => setShowJsonDialog(false)}
        disciplines={disciplines}
        defaultDocumentType={
          activeFilter === "policies"
            ? "policy"
            : activeFilter === "procedures"
              ? "procedure"
              : activeFilter === "instructions"
                ? "instruction"
                : activeFilter === "forms"
                  ? "form"
                  : activeFilter === "guidance"
                    ? "guidance"
                    : activeFilter === "record"
                      ? "record"
                      : activeFilter === "template"
                        ? "template"
                        : "instruction"
        }
        onImported={async () => {
          await fetchDisciplines();
          await fetchDocuments();
        }}
      />
    </HsePageChrome>
  );
};

